/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

export type UploadableFile = File & {
  processed?: boolean;
  progress?: number;
  validity?: boolean;
};

export type FileUploadHandler = (file: File, onProgress: (progressEvent: any) => void) => PromiseLike<any>;

export type FileUploadWarningMode = 'reference-data' | 'cdn-asset';

export interface FileUploadWarningOptions {
  mode: FileUploadWarningMode;
}

export function buildUploadWarnings(error: any, options: FileUploadWarningOptions): any[] {
  let warnings: any[] = [];
  const preventsAdServing = options.mode === 'cdn-asset';
  const isTimeout = options.mode === 'cdn-asset'
    ? !error.data || (error.status === -1 && error.data === null)
    : !error.data || error.status === -1;

  if (isTimeout) {
    warnings.push({
      text: options.mode === 'cdn-asset'
        ? "The upload failed to complete. Try uploading again or, if possible, refreshing the page to see which resources have been uploaded."
        : "The upload failed to complete. Try uploading again or updating the format of the data in the file you're trying to upload.",
      preventsAdServing
    });
    return warnings;
  }

  if (error.data.errors) {
    warnings = _.flatten(warnings.concat(_.map(error.data.errors, (value: any) => {
      const returnValue = _.cloneDeep(value.error);
      returnValue.preventsAdServing = preventsAdServing;
      return returnValue;
    })));
  } else if (error.data.map) {
    warnings = _.flatten(warnings.concat(_.map(error.data.map, (value: any) => {
      return {text: value, preventsAdServing};
    })));
  } else if (error.data) {
    warnings.push(error.data);
  }

  return warnings;
}

export function toUploadableFiles(fileList?: FileList | null): UploadableFile[] {
  return Array.from(fileList || []) as UploadableFile[];
}

export function buildFileLimitExceededWarning(
  fileUploadLimit: number,
  allFilesCount: number,
  singular: boolean
): any {
  return {
    text: singular
      ? 'Only {{fileUploadLimit}} file can be uploaded at a time. The last {{restFilesCount}} of the {{allFilesCount}} selected files were not uploaded.'
      : 'Only {{fileUploadLimit}} files can be uploaded at a time. The last {{restFilesCount}} of the {{allFilesCount}} selected files were not uploaded.',
    parameters: {
      fileUploadLimit,
      restFilesCount: allFilesCount - fileUploadLimit,
      allFilesCount
    },
    preventsAdServing: false
  };
}
